/**
 * Which way a Mira figure is better, for the comparisons that sort "best first" and name a Best and a
 * Worst (Hans, Track #2610112 X6c; the list confirmed by Taimoor on 2026-10-29, register
 * `mira-hans-review-2026-10-27.md`, "Best first").
 *
 * Kept in IRES Office on purpose: the Mira documents carry no direction (IRES Cloud keeps presentation
 * out), and the wording everywhere else stays neutral (C4): a change or a Markt position is never called
 * better or worse. A figure in neither list has no better direction (a total, a mix, Geräte je Projekt):
 * it sorts largest first and its levels read Höchster / Niedrigster.
 */

/** LOWER: less is better (a duration, a pause share, kWh per unit). HIGHER: more is better. */
export type MiraMetricDirection = "LOWER" | "HIGHER" | null;

/** Durations, the rejection rate, pause shares and the energy ratios. */
const LOWER_IS_BETTER: readonly string[] = [
  "MIRA-PRJ-002",
  "MIRA-PRJ-004",
  "MIRA-PRJ-008",
  "MIRA-PHA-012",
  "MIRA-PHA-013",
  "MIRA-PHA-014",
  "MIRA-PHA-015",
  "MIRA-PHA-016",
  "MIRA-PHA-017",
  "MIRA-PHA-018",
  "MIRA-PHA-019",
  "MIRA-PHA-020",
  "MIRA-PHA-021",
  "MIRA-PHA-022",
  "MIRA-INS-004",
  "MIRA-DRY-001",
  "MIRA-DRY-002",
  "MIRA-DRY-003",
  "MIRA-DRY-009",
  "MIRA-DRY-010",
  "MIRA-DRY-011",
  "MIRA-DEV-005",
  "MIRA-DEV-009",
  "MIRA-DEV-010",
];

/**
 * Projects, the fleet utilisation, the quotas, the coverage figures and the measuring frequency (DRY-005
 * until its documents are rewritten, DRY-012 Zwischenmessungen je Projekt, X5). The HyDry Control
 * figures (DEV-015, DEV-016, X7) have no direction, like Geräte je Projekt.
 */
const HIGHER_IS_BETTER: readonly string[] = [
  "MIRA-PRJ-001",
  "MIRA-DEV-006",
  "MIRA-DEV-011",
  "MIRA-DEV-012",
  "MIRA-DEV-013",
  "MIRA-DEV-014",
  "MIRA-INS-003",
  "MIRA-INS-005",
  "MIRA-DQ-001",
  "MIRA-DQ-002",
  "MIRA-DRY-005",
  "MIRA-DRY-012",
];

/** The direction of a figure, or `null` where none is better. */
export function metricDirection(metricId: string): MiraMetricDirection {
  if (LOWER_IS_BETTER.includes(metricId)) {
    return "LOWER";
  }
  return HIGHER_IS_BETTER.includes(metricId) ? "HIGHER" : null;
}

/**
 * Compares two values best first: ascending where lower is better, descending otherwise (a figure with no
 * direction sorts largest first, as every Mira list did before).
 */
export function compareBestFirst(direction: MiraMetricDirection, a: number, b: number): number {
  return direction === "LOWER" ? a - b : b - a;
}
